"use client"

import type { Hit } from "instantsearch.js"
import { useEffect, useRef } from "react"
import { useHits, useInstantSearch } from "react-instantsearch"

import useSearchSettled from "@lib/hooks/use-search-settled"
import { Text } from "@modules/common/components/ui"
import ProductCard from "@modules/amz/components/product-card"
import { indexedCurrency, priceAttribute } from "@lib/search-client"
import SkeletonProductGrid from "@modules/skeletons/templates/skeleton-product-grid"
import SearchPagination from "./pagination"
import { HitPricing } from "./price"

type ProductHit = Hit<
  {
    title: string | null
    handle: string | null
    thumbnail: string | null
    rating?: number | null
    reviews?: number | null
  } & HitPricing
>

type StoreHitsProps = {
  hitsPerPage: number
  currencyCode: string
}

const StoreHits = ({ hitsPerPage, currencyCode }: StoreHitsProps) => {
  const { items } = useHits<ProductHit>()
  const { status, error, indexUiState } = useInstantSearch()
  const { isSearching, hasNoResultsYet } = useSearchSettled()

  const { page, ...refinements } = indexUiState
  const currentPage = page ?? 1
  const refinementKey = JSON.stringify(refinements)

  const settled = useRef({ page: currentPage, refinementKey })

  useEffect(() => {
    if (!isSearching) {
      settled.current = { page: currentPage, refinementKey }
    }
  }, [isSearching, currentPage, refinementKey])

  const isPagingOnly =
    refinementKey === settled.current.refinementKey &&
    currentPage !== settled.current.page

  const showSkeleton = hasNoResultsYet || (isSearching && isPagingOnly)

  if (status === "error") {
    return (
      <Text
        className="py-16 text-center text-ui-fg-error"
        data-testid="products-error"
      >
        No se pudieron cargar los productos
        {error?.message ? `: ${error.message}` : "."}
      </Text>
    )
  }

  return (
    <>
      {showSkeleton ? (
        <SkeletonProductGrid numberOfProducts={hitsPerPage} />
      ) : !items.length ? (
        <Text
          className="py-16 text-center text-zl-steel border border-zl-line bg-white"
          data-testid="no-products"
        >
          No hay resultados para estos filtros. Prueba con otra búsqueda.
        </Text>
      ) : (
        <ul
          className="zl-grid grid-cols-2 w-full small:grid-cols-3"
          data-testid="products-list"
        >
          {items.map((hit) => {
            if (!hit.handle) {
              return null
            }
            const min = hit[priceAttribute("min_price", currencyCode)]
            const original = hit[priceAttribute("original_price", currencyCode)]
            const onSale = hit[priceAttribute("on_sale", currencyCode)] === true
            return (
              <li key={hit.objectID}>
                <ProductCard
                  handle={hit.handle}
                  title={hit.title ?? ""}
                  thumbnail={hit.thumbnail}
                  amount={typeof min === "number" ? min : null}
                  compareAt={onSale && typeof original === "number" ? original : null}
                  currencyCode={indexedCurrency(currencyCode)}
                  rating={hit.rating}
                  reviews={hit.reviews}
                />
              </li>
            )
          })}
        </ul>
      )}
      <SearchPagination />
    </>
  )
}

export default StoreHits
